/* eslint-disable no-underscore-dangle */
import { CONFIG } from 'config';
import {
  useChurnZero,
  useHubspotScript,
  useUserPilotScript,
  useWorkNetScript,
} from 'hooks/importScript';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { sendChurnZeroEvent, setChurnZeroContact } from 'helpers/churnZero';
import { CHURN_ZERO_EVENT_NAMES, CONSTANTS } from 'constants/common';
import {
  useCurrentAccount,
  useCurrentCompany,
  useCurrentUser,
  useSpoof,
} from 'hooks/api';
import { useAuth0 } from '@auth0/auth0-react';
import { useStripeCustomer } from 'hooks/api/useStripe';
import useContacts from 'hooks/api/useContacts';
import { useLocation } from 'react-router-dom';
import { useDataSourcesFeatureFlag } from 'hooks/useFeatureFlag';
import { useBankAccounts } from 'hooks/api/bankAccounts';
import { getBankAccountDataSummary } from 'helpers/userpilot';
import { useCurrentHouseholdMember } from 'hooks/api/useHousehold';

export const LoadHubspotIdentify = () => {
  const { isAuthenticated } = useAuth0();
  const { loginUser } = useCurrentUser();
  const { isAdminSpoof } = useSpoof();

  useEffect(() => {
    if (isAuthenticated && loginUser && (window as any)._hsq) {
      (window as any)._hsq.push([
        'identify',
        {
          email: loginUser?.email,
        },
      ]);
    }
  }, [loginUser, isAdminSpoof, isAuthenticated]);

  return null;
};

export const LoadHubspotScript = () => {
  useHubspotScript();
  const location = useLocation();
  useEffect(() => {
    if ((window as any)._hsq) {
      (window as any)._hsq.push(['setPath', location.pathname]);
      (window as any)._hsq.push(['trackPageView']);
    }
  }, [location]);
  const { isAuthenticated } = useAuth0();
  if (!isAuthenticated || !(window as any)._hsq) {
    return null;
  }
  return <LoadHubspotIdentify />;
};

export const LoadChurnScript = () => {
  const status = useChurnZero();
  useEffect(() => {
    if (status === 'ready') {
      const churnZero = (window as any).ChurnZero;
      churnZero.push(['setAppKey', CONFIG.churnAppKey]); // AppKey from ChurnZero
    }
  }, [status]);
  return null;
};

export const ChurnZeroPageTracking = () => {
  useEffect(() => {
    sendChurnZeroEvent(CHURN_ZERO_EVENT_NAMES.VISIT_PAGE, window.location.href);
  }, [window.location.href]);

  return null;
};

export const ChurnZeroSetupContact = ({ children }: PropsWithChildren<{}>) => {
  const { currentAccount } = useCurrentAccount();
  useEffect(() => {
    if (currentAccount && currentAccount.hubspotContactId) {
      setChurnZeroContact(currentAccount.hubspotContactId).catch((err) => {
        console.error(err);
      });
    }
  }, [currentAccount]);

  return <>{children}</>;
};

export const LoadUserpilotIdentify = () => {
  const { isAuthenticated } = useAuth0();
  const { currentUser } = useCurrentUser();
  const { currentAccount } = useCurrentAccount();
  const { currentCompany } = useCurrentCompany();
  const { isAdminSpoof } = useSpoof();
  const { bankAccounts } = useBankAccounts(currentCompany?.id);
  const primaryStripeId = currentAccount?.primaryStripeId;
  const { stripeCustomers } = useStripeCustomer(currentAccount?.id);
  const { currentHouseholdMember } = useCurrentHouseholdMember();
  const { data: contactData } = useContacts.GetContact(
    currentAccount?.hubspotContactId ?? '',
  );
  const dataSource = useDataSourcesFeatureFlag();
  const primaryStripeCustomer = useMemo(() => {
    if (stripeCustomers == null) return null;
    if (stripeCustomers.length === 1) return stripeCustomers[0];
    const primary = stripeCustomers.find((x) => x.stripeId === primaryStripeId);
    return primary ?? null;
  }, [stripeCustomers, primaryStripeId]);

  useEffect(() => {
    if (
      isAuthenticated &&
      (window as any).profitwell &&
      currentUser &&
      currentAccount &&
      currentUser?.role === CONSTANTS.USER_ROLES.CUSTOMER &&
      !isAdminSpoof &&
      CONFIG.enableProfitWell
    ) {
      (window as any).profitwell('user_email', currentUser.email);
    }
  }, [currentAccount, currentUser, isAdminSpoof, isAuthenticated]);

  useEffect(() => {
    if (
      isAuthenticated &&
      (window as any).userpilot &&
      currentUser &&
      currentAccount
    ) {
      // Create a safe object with only primitive values
      const userpilotData: Record<string, string | number | boolean | null> = {
        id: currentAccount.id,
        email: currentUser.email ?? '',
        role: currentUser.role ?? '',
        hubspotId: contactData?.hubspotId ?? '',
        dataSource: dataSource ?? '',
        stripePlan: primaryStripeCustomer?.plan ?? '',
        csmEmail: currentAccount.csmInfo?.email ?? '',
      };

      // Add company data if available
      if (currentCompany) {
        userpilotData.companyId = currentCompany.id ?? '';
        userpilotData.companyName = currentCompany.name ?? '';
        userpilotData.companyEntityType = currentCompany.entityType ?? '';
        userpilotData.companyStatus = currentCompany.status ?? '';
        userpilotData.companyCreatedAt = currentCompany.createdAt ?? '';
        userpilotData.companyUpdatedAt = currentCompany.updatedAt ?? '';
      }

      // Add household member data if available
      if (currentHouseholdMember) {
        userpilotData.householdMemberName = String(
          currentHouseholdMember.name ?? '',
        );
        userpilotData.householdMemberEmail = currentHouseholdMember.email ?? '';
        userpilotData.householdMemberRelationshipToOwner =
          currentHouseholdMember.relationshipToOwner ?? '';
        userpilotData.householdMemberDob = currentHouseholdMember.dob ?? '';
      }

      // Add bank account summary data
      const bankSummary = getBankAccountDataSummary(bankAccounts);
      if (bankSummary) {
        // Only include primitive values from bank summary
        Object.entries(bankSummary).forEach(([key, value]) => {
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            userpilotData[`bank_${key}`] = value;
          }
        });
      }

      if (currentUser?.role === CONSTANTS.USER_ROLES.CUSTOMER) {
        if (isAdminSpoof) {
          // do not send info when spoofing as admin
          return;
        }
        (window as any).userpilot.identify(currentAccount.id, userpilotData);
      } else {
        (window as any).userpilot.identify(currentAccount.id, {
          email: currentUser.email ?? '',
          role: currentUser.role ?? '',
        });
      }
    }
  }, [
    isAuthenticated,
    currentUser,
    currentCompany,
    currentAccount,
    stripeCustomers,
    contactData,
    dataSource,
    primaryStripeCustomer?.plan,
    isAdminSpoof,
    bankAccounts,
    currentHouseholdMember,
  ]);
  return null;
};

export const LoadWorknetScript = ({
  accountId,
  hubspotId,
}: {
  accountId: string;
  hubspotId: string;
}) => {
  useWorkNetScript(accountId, hubspotId);
  return null;
};

export const LoadUserPilotScript = () => {
  useUserPilotScript();
  const location = useLocation();
  useEffect(() => {
    if ((window as any).userpilot) {
      (window as any).userpilot.reload();
    }
  }, [location]);
  const { isAuthenticated } = useAuth0();
  if (!isAuthenticated || !(window as any).userpilot) {
    return null;
  }
  return <LoadUserpilotIdentify />;
};
